import { ColumnDef } from '@tanstack/react-table';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import { DataTableColumnHeader } from '@/components/core';
import type { ColumnFilterOption } from '@/lib/table-query';
import { Button } from '@/components/ui-kit/button';
import { Holiday } from '../../types/holiday.types';
import { formatDateOnly, weekdayNameOf } from '@/lib/utils/custom-date/custom-date';
import { holidayDay } from '../../utils/holiday-days';

interface CreateHolidayTableColumnsProps {
  t: (key: string) => string;
  canEdit: boolean;
  canDelete: boolean;
  /** Whether to show the cantons column (for roles that can set cantons, or once a row has some). */
  showCantons: boolean;
  /** The cantons a holiday applies to, by name, or the "nationwide" label. */
  getCantonLabel: (holiday: Holiday) => string;
  /** The organization's cantons, for the cantons column's filter. */
  cantonOptions: ColumnFilterOption[];
  onEdit: (holiday: Holiday) => void;
  onDelete: (holiday: Holiday) => void;
}

export const createHolidayTableColumns = ({
  t,
  canEdit,
  canDelete,
  showCantons,
  getCantonLabel,
  cantonOptions,
  onEdit,
  onDelete,
}: CreateHolidayTableColumnsProps): ColumnDef<Holiday>[] => {
  const columns: ColumnDef<Holiday>[] = [
    {
      id: 'Name',
      meta: { filter: { type: 'text' } },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('HOLIDAY_NAME')} />,
      accessorFn: (row) => row.Name,
    },
    {
      id: 'StartDate',
      meta: { filter: { type: 'date' } },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('DATE')} />,
      // The holiday's day, as "YYYY-MM-DD" so it also sorts correctly; shown as it is, never shifted.
      accessorFn: (row) => holidayDay(row),
      cell: ({ row }) => formatDateOnly(row.original.StartDate),
    },
    {
      id: 'Day',
      header: () => <span className="text-xs font-medium">{t('DAY')}</span>,
      enableSorting: false,
      // Weekday of the holiday's calendar day (date only, never zone-converted).
      accessorFn: (row) => weekdayNameOf(holidayDay(row)) ?? '',
      cell: ({ getValue }) => {
        const weekday = getValue<string>();
        return weekday ? t(weekday) : '';
      },
    },
  ];

  if (showCantons) {
    columns.push({
      id: 'CantonIds',
      // Filters holidays stored with any of the picked cantons; a list of ids has no useful sort.
      enableSorting: false,
      meta: { filter: { type: 'options', options: cantonOptions } },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('CANTONS')} />,
      accessorFn: (row) => getCantonLabel(row),
    });
  }

  if (canEdit || canDelete) {
    columns.push({
      id: 'actions',
      // Card: top right, as the old portal's card header.
      meta: { mobile: 'badge' as const },
      header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(row.original)}
              aria-label={t('EDIT')}
            >
              <PencilSimple className="size-4" />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(row.original)}
              aria-label={t('DELETE')}
            >
              <Trash className="size-4" />
            </Button>
          )}
        </div>
      ),
    });
  }

  return columns;
};
